/**
 * @function customContainerMdPlugin
 * This is a markdown-it custom plugin to detect if there's a specific markdown pattern to render a base tips component
 *
 * @param {object} md markdown context
 * @param {string} name markdown pattern identifier
 * @param {object} options additional options
 * @property {function} options.validate custom validate function
 * @property {fucntion} options.render custom renderer function
 */
const customContainerMdPlugin = (md, name, options = {}) => {
  function defaultValidation(params) {
    return params.trim().split(' ', 2)[0] === name
  }

  function defaultRender(tokens, idx, _options, env, self) {
    // add a class to the opening tag
    if (tokens[idx].nesting === 1) {
      tokens[idx].attrPush(['class', name])
    }

    return self.renderToken(tokens, idx, _options, env, self)
  }

  const minMarkers = 3
  const marker = options.marker || ':'
  const markerChar = marker.charCodeAt(0)
  const markerLength = marker.length
  const validateFunc = options.validate || defaultValidation
  const renderFunc = options.render || defaultRender

  const customContainerRenderer = (state, startLine, endLine, silent) => {
    let position
    let autoClosed = false
    let start = state.bMarks[startLine] + state.tShift[startLine]
    let max = state.eMarks[startLine]

    // check first char for the markdown pattern
    if (markerChar !== state.src.charCodeAt(start)) return false

    for (position = start + 1; position <= max; position++) {
      if (marker[(position - start) % markerLength] !== state.src[position]) {
        break
      }
    }

    const markerCount = Math.floor((position - start) / markerLength)
    if (markerCount < minMarkers) return false
    position -= (position - start) % markerLength

    const markup = state.src.slice(start, position)
    const params = state.src.slice(position, max)
    if (!validateFunc(params)) return false

    // since start found, we can report success here in validation mode
    if (silent) return true

    // search for the end of the block
    let nextLine = startLine
    for (;;) {
      nextLine++
      // unclosed block should be autoclosed by end of document.
      // also block seems to be autoclosed by end of parent
      if (nextLine >= endLine) break

      start = state.bMarks[nextLine] + state.tShift[nextLine]
      max = state.eMarks[nextLine]

      // non-empty line with negative indent should stop the list:
      // - ```
      //  test
      if (start < max && state.sCount[nextLine] < state.blkIndent) break

      // continue if string is not a marker
      if (markerChar !== state.src.charCodeAt(start)) continue
      // closing fence should be indented less than 4 spaces
      if (state.sCount[nextLine] - state.blkIndent >= 4) continue

      for (position = start + 1; position <= max; position++) {
        if (marker[(position - start) % markerLength] !== state.src[position]) {
          break
        }
      }

      // closing code fence must be at least as long as the opening one
      if (Math.floor((position - start) / markerLength) < markerCount) continue

      // make sure tail has spaces only
      position -= (position - start) % markerLength
      position = state.skipSpaces(position)

      if (position < max) continue

      autoClosed = true
      break
    }

    const oldParent = state.parentType
    const oldLineMax = state.lineMax
    state.parentType = 'custom_container'

    // this will prevent lazy continuations from ever going past our end marker
    state.lineMax = nextLine

    let token = state.push(`custom_container_${name}_open`, 'div', 1)
    token.markup = markup
    token.block = true
    token.info = params
    token.map = [startLine, nextLine]

    state.md.block.tokenize(state, startLine + 1, nextLine)

    token = state.push(`custom_container_${name}_close`, 'div', -1)
    token.markup = state.src.slice(start, position)
    token.block = true

    state.parentType = oldParent
    state.lineMax = oldLineMax
    state.line = nextLine + (autoClosed ? 1 : 0)

    return true
  }

  md.block.ruler.before(
    'fence',
    `custom_container_${name}`,
    customContainerRenderer,
    {
      alt: ['paragraph', 'reference', 'blockquote', 'list'],
    }
  )
  md.renderer.rules[`custom_container_${name}_open`] = renderFunc
  md.renderer.rules[`custom_container_${name}_close`] = renderFunc
}

export default customContainerMdPlugin
